/**
 * Display helpers for lead facts, read straight from the lead's own columns (the
 * source of truth — suggestion rows only carry the nudge lifecycle). Contact facts
 * (phone/email) are rendered separately, so they are excluded here.
 */

import type { ILead } from 'src/services/api';
import type { LeadFactKey } from 'src/services/api/leads/lead-facts';
import { VOUCHER_TYPE_OPTIONS } from 'src/services/api/leads/lead-facts';

/** Shown in place of the value for a fact the lead has no value for; clicking it opens
 * the same editor, so an un-extracted fact can still be filled in by hand. */
export const EMPTY_VALUE_PLACEHOLDER = 'Add';

export type DisplayedFact = {
    key: LeadFactKey;
    label: string;
    /** Formatted display value (income shows both cadences, pet sentinels are worded);
     * empty when the column is unset. */
    value: string;
    /** The raw column value, for the editor; empty when the column is unset. */
    rawValue: string;
    /** Whether the lead's column actually holds a value. */
    isSet: boolean;
};

/* Sidebar display order. */
const DISPLAYED_FACT_KEYS = ['annual_income', 'household_size', 'pet_type', 'voucher_type'] as const;

/* The facts worth an empty "Add" row: the ones a landlord routinely fills in by hand
   when nothing was extracted. Every other key (voucher type today) shows only once it
   has a value, so the card does not grow a placeholder per fact key we add. */
const PLACEHOLDER_FACT_KEYS = new Set<LeadFactKey>(['annual_income', 'household_size', 'pet_type']);

const DISPLAY_LABELS: Partial<Record<LeadFactKey, string>> = {
    annual_income: 'Income',
    household_size: 'Household Size',
    pet_type: 'Pet',
    voucher_type: 'Voucher Type',
};

/* Numeric value of a raw money string (e.g. "5,000" or "$5,000"), or undefined if it
   isn't a finite number. */
function parseMoney(raw: string): number | undefined {
    const n = Number(raw.replace(/[,$\s]/g, ''));
    return raw.trim() !== '' && Number.isFinite(n) ? n : undefined;
}

/* Comma-grouped dollar amount (e.g. $5,000), matching how property rent is shown. */
export function formatMoney(raw: string): string {
    const n = parseMoney(raw);
    return n === undefined ? raw : `$${n.toLocaleString()}`;
}

/** Income is stored annually; the display always shows both cadences
 * ($60,000/yr | $5,000/mo). Monthly is derived (rounded). */
export function formatIncome(annual: string): string {
    const n = parseMoney(annual);
    if (n === undefined) {
        return annual;
    }
    return `${formatMoney(String(n))}/yr | ${formatMoney(String(Math.round(n / 12)))}/mo`;
}

/** voucher_type is stored as an enum value; show its human label (raw value if unknown). */
export function formatVoucherType(value: string): string {
    return VOUCHER_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function formatFactValue(key: LeadFactKey, value: string): string {
    if (key === 'annual_income') {
        return formatIncome(value);
    }
    if (key === 'voucher_type') {
        return formatVoucherType(value);
    }
    return value;
}

/** The fact columns shown in Lead Info, in display order: every set column, plus the
 * placeholder keys when unset so they can be filled in manually. */
export function getDisplayedFacts(lead: ILead): DisplayedFact[] {
    return DISPLAYED_FACT_KEYS.flatMap((key) => {
        const value = lead[key];
        const isSet = value !== null && value !== undefined && String(value) !== '';
        if (!isSet && !PLACEHOLDER_FACT_KEYS.has(key)) {
            return [];
        }
        const rawValue = isSet ? String(value) : '';
        return [
            {
                key,
                label: DISPLAY_LABELS[key] ?? key,
                value: isSet ? formatFactValue(key, rawValue) : '',
                rawValue,
                isSet,
            },
        ];
    });
}

/** The fact columns that actually hold a value (share text, section auto-expand). */
export function getSetFacts(lead: ILead): DisplayedFact[] {
    return getDisplayedFacts(lead).filter(({ isSet }) => isSet);
}
